import Modal from 'core/components/dialogs/Modal'
import useWallet from 'core/hooks/wallet/useWallet'
import { useSettings } from 'core/hooks/settings/useSettings'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { usePayPeriods, useUserProfile } from 'core/hooks/data/useDataHook'
import { ID } from 'types/utility'
import FundingMethods from 'core/widgets/funding-modal/funding-methods/FundingMethods'
import EditAmount from 'core/widgets/funding-modal/edit-amount/EditAmount'
import ACHCreditWire from 'core/widgets/funding-modal/ach-credit-wire/ACHCreditWire'
import AchDebit from 'core/widgets/funding-modal/ach-debit/AchDebit'
import FundingAutoPay from 'core/widgets/funding-modal/FundingAutoPay'
import FundingSuccessModal from 'core/widgets/funding-modal/FundingSuccessModal'

export interface FundingModalFormValues {
  view:
    | 'funding-methods'
    | 'edit-amount'
    | 'add-funding-account'
    | 'ach-credit'
    | 'wire-transfer'
  funding_method?: 'ach-debit' | 'ach-credit' | 'wire-transfer'
  amountType: 'selected-pay-periods' | 'all-pay-periods' | 'other-amount'
  enterAmountMode: boolean
  selectPayPeriodsMode: boolean
  amount: number
  customAmount: string
  previewSelectedPayPeriods: ID[]
  selected_pay_periods: ID[]
  account_id: ID
  auto_pay: boolean
  fundingInitiated: boolean
}

const FundingModal = () => {
  const { hasWallet } = useWallet()
  const {
    set,
    data: {
      fundingModal: { show }
    }
  } = useSettings()
  const { data: user } = useUserProfile()
  const { data: payPeriods } = usePayPeriods({
    params: {
      status: 'active',
      page: 1,
      pageSize: 100
    },
    opts: {
      enabled: !!user?.token_id,
      staleTime: Infinity
    }
  })

  const methods = useForm<FundingModalFormValues>({
    defaultValues: {
      view: 'funding-methods',
      funding_method: undefined,
      amountType: 'selected-pay-periods',
      enterAmountMode: false,
      selectPayPeriodsMode: false,
      amount: 0,
      customAmount: '$0.00',
      previewSelectedPayPeriods: [],
      selected_pay_periods: [],
      account_id: '',
      auto_pay: true,
      fundingInitiated: false
    }
  })

  const { watch, setValue } = methods

  const [view, fundingMethod, fundingInitiated] = watch([
    'view',
    'funding_method',
    'fundingInitiated'
  ])

  const title = useMemo(() => {
    if (view === 'add-funding-account') return undefined
    if (view === 'ach-credit') return 'ACH Credit transfer'
    if (view === 'wire-transfer') return 'Wire transfer'
    return 'Deposit money'
  }, [view])

  const onBack = useMemo(() => {
    const inFirstStep =
      view === 'funding-methods' && fundingMethod !== 'ach-debit'

    if (fundingInitiated || inFirstStep) return undefined

    return () => {
      if (fundingMethod !== undefined && view !== 'add-funding-account') {
        setValue('funding_method', undefined)
      }
      setValue('view', 'funding-methods')
    }
  }, [view, fundingMethod, fundingInitiated, setValue])

  const showAutoPay = useMemo(() => {
    return (
      fundingInitiated === false &&
      !!fundingMethod &&
      ['funding-methods', 'ach-credit', 'wire-transfer'].includes(view)
    )
  }, [fundingInitiated, fundingMethod, view])

  useEffect(() => {
    if (payPeriods?.data?.length) {
      setValue('previewSelectedPayPeriods', [payPeriods.data[0].id as string])
      setValue('selected_pay_periods', [payPeriods.data[0].id])
    }
  }, [payPeriods, setValue])

  if (!hasWallet) return null

  return (
    <FormProvider {...methods}>
      <FundingSuccessModal />
      <Modal
        className="max-h-max"
        show={show}
        title={title}
        onBack={onBack}
        onClose={() => {
          set('fundingModal', {
            show: false
          })
        }}
      >
        {view === 'funding-methods' && <FundingMethods />}
        {view === 'edit-amount' && <EditAmount />}
        {view === 'add-funding-account' && <AchDebit />}
        {(view === 'ach-credit' || view === 'wire-transfer') && (
          <ACHCreditWire />
        )}
        {showAutoPay && <FundingAutoPay />}
      </Modal>
    </FormProvider>
  )
}

export default FundingModal
